import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { AuthContext } from "../../../context/AuthContext";
import { isFreelance, isProjectManager } from "../../../utils/constants/accountType";
import { PROJECT_STATUSES, projectStatusStyle } from "../../../utils/constants/projectStatus";
import { JOB_STATUSES, jobStatusStyle } from "../../../utils/constants/jobStatus";
import Panel from "../../../components/Dashboard/Panel";
import StatTile from "../../../components/Dashboard/StatTile";
import StatusBreakdown from "../../../components/Dashboard/StatusBreakdown";
import DataTable from "../../../components/Dashboard/DataTable";
import TrendBars from "../../../components/Dashboard/TrendBars";
import Badge from "../../../components/Dashboard/Badge";
import PageHeader from "../../../components/Dashboard/PageHeader";
import {
  countBy,
  daysUntil,
  displayNameOf,
  dueLabel,
  entityName,
  fmtDate,
  greetingFor,
  monthlyCounts,
  scopeLabel,
} from "../../../components/Dashboard/dashboardKit";
import { Users, Briefcase, Folder, Layers, ClipboardList, Wallet } from "lucide-react";

// How many rows each list endpoint is asked for. The counts still come from
// `meta.total`; this only decides how much the breakdown and deadline panels
// have to work with, and every panel says which slice it is describing.
const SAMPLE = 50;

// A project past either of these is done with, so it never belongs in a list of
// what is coming up.
const CLOSED_PROJECT_STATUSES = ["Submission Accepted", "Cancelled", "Offer Rejected"];

const viewAll = (to) => (
  <Link to={to} className="text-xs font-semibold text-brand-600 hover:text-brand-700 hover:underline">
    View all
  </Link>
);

const DueChip = ({ days }) => {
  const tone =
    days < 0
      ? "bg-rose-50 text-rose-600"
      : days <= 2
      ? "bg-amber-50 text-amber-700"
      : "bg-gray-100 text-gray-600";
  return <Badge className={tone}>{dueLabel(days)}</Badge>;
};

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  // Freelance accounts have no managers, vendors, client pools or jobs - hide
  // those cards/actions and skip their (enterprise-only) endpoints.
  const freelance = isFreelance(user);
  // Project managers work inside an admin's workspace: no manager or client-pool
  // module (both 403 for them) and no create/edit access to vendors. Everything
  // else is scoped server-side to their client pools.
  const projectManager = isProjectManager(user);
  const showManagers = !freelance && !projectManager;
  const showClientPools = !freelance && !projectManager;
  const showVendors = !freelance;
  const showJobs = !freelance;
  // Freelance accounts get Quotations and a Finances hub in place of the team modules.
  const showQuotations = freelance;
  const showFinances = freelance;

  const [counts, setCounts] = useState({
    managers: null, clients: null, vendors: null, clientPools: null, projects: null, jobs: null, quotations: null,
  });
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const countOf = (res) =>
      res?.data?.meta?.total ?? (Array.isArray(res?.data?.data) ? res.data.data.length : 0);

    // Endpoints the current role has no access to are skipped entirely; the
    // rest are fetched independently so one failure can't blank the whole
    // dashboard (a rejected Promise.all would leave every card at zero).
    const skip = Promise.resolve(null);
    const get = (url) =>
      api.get(url).catch((err) => {
        console.error(`Dashboard: ${url} failed`, err);
        return null;
      });

    const fetchAll = async () => {
      try {
        const [managersRes, clientsRes, vendorsRes, clientPoolsRes, projectsRes, jobsRes, quotationsRes] =
          await Promise.all([
            showManagers ? get("/managers") : skip,
            get("/clients"),
            showVendors ? get("/vendors") : skip,
            showClientPools ? get("/client-pools") : skip,
            get(`/projects?limit=${SAMPLE}`),
            showJobs ? get(`/jobs?limit=${SAMPLE}`) : skip,
            showQuotations ? get("/quotations?limit=1") : skip,
          ]);

        setCounts({
          managers:    managersRes ? countOf(managersRes) : null,
          clients:     clientsRes ? countOf(clientsRes) : null,
          vendors:     vendorsRes ? countOf(vendorsRes) : null,
          clientPools: clientPoolsRes ? countOf(clientPoolsRes) : null,
          projects:    projectsRes ? countOf(projectsRes) : null,
          jobs:        jobsRes ? countOf(jobsRes) : null,
          quotations:  quotationsRes ? countOf(quotationsRes) : null,
        });

        setProjects(projectsRes?.data?.data ?? []);
        setJobs(jobsRes?.data?.data ?? []);
      } catch (err) {
        console.error("AdminDashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [showManagers, showClientPools, showVendors, showJobs, showQuotations]);

  const projectBreakdown = countBy(projects, "status", PROJECT_STATUSES).map(({ key, count }) => ({
    label: key,
    count,
    dot: projectStatusStyle(key).dot,
  }));

  const jobBreakdown = countBy(jobs, "status", JOB_STATUSES).map(({ key, count }) => ({
    label: key,
    count,
    dot: jobStatusStyle(key).dot,
  }));

  const monthlyProjects = monthlyCounts(projects, {
    truncated: counts.projects != null && projects.length < counts.projects,
  });

  const deadlines = projects
    .filter((p) => p.deadline_at && !CLOSED_PROJECT_STATUSES.includes(p.status))
    .sort((a, b) => new Date(a.deadline_at) - new Date(b.deadline_at))
    .slice(0, 5);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Dashboard"
        subtitle={`${greetingFor(displayNameOf(user, "Admin"))}. Here is where your workspace stands.`}
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        {showManagers && (
          <StatTile icon={Users} label="Managers" value={counts.managers} tone="brand" to="/managers" loading={loading} />
        )}
        <StatTile icon={Users} label="Clients" value={counts.clients} tone="violet" to="/clients" loading={loading} />
        {showVendors && (
          <StatTile icon={Users} label="Vendors" value={counts.vendors} tone="indigo" to="/vendors" loading={loading} />
        )}
        {showClientPools && (
          <StatTile icon={Layers} label="Client pools" value={counts.clientPools} tone="cyan" to="/client-pools" loading={loading} />
        )}
        <StatTile icon={Folder} label="Projects" value={counts.projects} tone="emerald" to="/projects" loading={loading} />
        {showQuotations && (
          <StatTile icon={ClipboardList} label="Quotations" value={counts.quotations} tone="cyan" to="/quotations" loading={loading} />
        )}
        {showFinances && (
          <StatTile icon={Wallet} label="Finances" hint="Receivables, invoices, payments" tone="amber" to="/finances" />
        )}
        {showJobs && (
          <StatTile icon={Briefcase} label="Jobs" value={counts.jobs} tone="amber" to="/jobs" loading={loading} />
        )}
      </div>

      {/* Modules. Each row of the grid pairs a wide panel with a narrow one. */}
      <div className="grid gap-4 xl:grid-cols-3">
        <Panel
          className="xl:col-span-2"
          title="Projects added"
          subtitle="Per month, over the last six months"
          action={viewAll("/projects")}
        >
          <TrendBars
            points={monthlyProjects}
            loading={loading}
            empty="No projects created yet."
          />
        </Panel>

        <Panel
          title="Project pipeline"
          subtitle={scopeLabel(projects.length, counts.projects, "projects")}
          action={viewAll("/projects")}
        >
          <StatusBreakdown
            items={projectBreakdown}
            loading={loading}
            empty="No projects to break down yet."
          />
        </Panel>

        <Panel
          className="xl:col-span-2"
          title="Recent projects"
          subtitle="The five most recently created"
          action={viewAll("/projects")}
        >
          <DataTable
            loading={loading}
            rows={projects.slice(0, 5)}
            empty="No projects yet."
            columns={[
              {
                key: "name",
                header: "Project",
                cell: (p) => (
                  <Link
                    to={`/project/${p.id}`}
                    className="block max-w-[13rem] truncate text-sm font-medium text-gray-800 hover:text-brand-600"
                  >
                    {p.project_name}
                  </Link>
                ),
              },
              {
                key: "client",
                header: "Client",
                cell: (p) =>
                  p.client ? (
                    <Link
                      to={`/clients/${p.client.id}`}
                      className="block max-w-[10rem] truncate text-sm text-gray-500 hover:text-brand-600"
                    >
                      {entityName(p.client) ?? `CLT-${String(p.client.id).padStart(3, "0")}`}
                    </Link>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  ),
              },
              {
                key: "due",
                header: "Deadline",
                className: "hidden sm:table-cell",
                cell: (p) => <span className="text-sm text-gray-500">{fmtDate(p.deadline_at)}</span>,
              },
              {
                key: "status",
                header: "Status",
                cell: (p) => <Badge className={projectStatusStyle(p.status).badge}>{p.status}</Badge>,
              },
            ]}
          />
        </Panel>

        <Panel
          title="Upcoming deadlines"
          subtitle="Open projects, soonest first"
          action={viewAll("/projects")}
        >
          {loading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-lg bg-gray-50" />
              ))}
            </div>
          ) : deadlines.length === 0 ? (
            <p className="py-2 text-sm text-gray-400">Nothing due on the open projects.</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {deadlines.map((p) => {
                const days = daysUntil(p.deadline_at);
                return (
                  <li key={p.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                      <Link
                        to={`/project/${p.id}`}
                        className="block truncate text-sm font-medium text-gray-800 hover:text-brand-600"
                      >
                        {p.project_name}
                      </Link>
                      <p className="mt-0.5 truncate text-xs text-gray-400">
                        {entityName(p.client) ?? "No client"} · {fmtDate(p.deadline_at)}
                      </p>
                    </div>
                    <DueChip days={days} />
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        {!freelance && (
          <>
            <Panel
              className="xl:col-span-2"
              title="Recent jobs"
              subtitle="The five most recently created"
              action={viewAll("/jobs")}
            >
              <DataTable
                loading={loading}
                rows={jobs.slice(0, 5)}
                empty="No jobs yet."
                columns={[
                  {
                    key: "name",
                    header: "Job",
                    cell: (j) =>
                      j.project?.id ? (
                        <Link
                          to={`/project/${j.project.id}/job/${j.id}`}
                          className="block max-w-[13rem] truncate text-sm font-medium text-gray-800 hover:text-brand-600"
                        >
                          {j.name}
                        </Link>
                      ) : (
                        <span className="block max-w-[13rem] truncate text-sm font-medium text-gray-800">
                          {j.name}
                        </span>
                      ),
                  },
                  {
                    key: "vendor",
                    header: "Vendor",
                    cell: (j) =>
                      j.vendor ? (
                        <Link
                          to={`/vendors/${j.vendor.id}`}
                          className="block max-w-[10rem] truncate text-sm text-gray-500 hover:text-brand-600"
                        >
                          {entityName(j.vendor) ?? `VEN-${String(j.vendor.id).padStart(3, "0")}`}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      ),
                  },
                  {
                    key: "due",
                    header: "Deadline",
                    className: "hidden sm:table-cell",
                    cell: (j) => <span className="text-sm text-gray-500">{fmtDate(j.deadline_at)}</span>,
                  },
                  {
                    key: "status",
                    header: "Status",
                    cell: (j) => <Badge className={jobStatusStyle(j.status).badge}>{j.status}</Badge>,
                  },
                ]}
              />
            </Panel>

            <Panel
              title="Job status"
              subtitle={scopeLabel(jobs.length, counts.jobs, "jobs")}
              action={viewAll("/jobs")}
            >
              <StatusBreakdown
                items={jobBreakdown}
                loading={loading}
                empty="No jobs to break down yet."
              />
            </Panel>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
